import * as React from 'react'

import { BordersStyles } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimesheetEditTimesheetRequestFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'

import { type GroupedByDateWorkedBreaks } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/types'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { useFetchBreakDurationRules } from 'modules/attendance/hooks/useFetchBreakDurationRules'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import BreakSettingsUtils from 'modules/attendance/lib/utils/BreakSettings'
import validateBreakDurationRules, {
  isBreakSetting,
} from 'modules/attendance/lib/validateShiftBreakDurationRules'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

import RowPanel from './RowPanel'
import RowTab from './RowTab'

interface ShiftRowAccordionProps {
  store: Store
  open: boolean
  dateKey: string
  className: string
  date: moment.Moment
  isBeforeToday: boolean
  hasFutureDate: boolean
  showHourBalance: boolean
  calendarDay: CalendarDay
  hasUnresolvedShift: boolean
  workedBreaks: GroupedByDateWorkedBreaks
  toggle: (value: boolean) => () => void
  overtimeRequestMinutes: number
  allBreaks: BreakConfigurationFragment[]
  hasEditTimesheetAuthorization: boolean
  dayBreaks: AttendanceBreakConfigurations[]
  borderLeftStyleDirection: BordersStyles
  balanceMinutes: number
  shiftLocations?: AttendanceShiftLocationNodes
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  editTimesheetRequests: TimesheetEditTimesheetRequestFragment[]
  setBackground: (
    calendarDay: CalendarDay,
    isTab?: boolean
  ) => 'champagne100' | 'grey100' | 'grey000White'
  inconsistencies?: Inconsistencies
  showStartsNextDayCheckbox: boolean
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  workedMinutes: number
  effectiveWorkedMinutes: number
  breaksMinutesRounded: number
  hasCorrectiveBreaks: boolean
  hasExtraHourManagementV2: boolean
  showExtraHoursColumn: boolean
  showComplementaryHoursColumn: boolean
  workedMinutesNotApproved: number
  shifts: TimesheetPageShiftFragment[]
}

const ShiftRowAccordion = ({
  open,
  date,
  store,
  toggle,
  dateKey,
  className,
  dayBreaks,
  allBreaks,
  calendarDay,
  workedBreaks,
  hasFutureDate,
  setBackground,
  isBeforeToday,
  shiftLocations,
  balanceMinutes,
  showHourBalance,
  attendanceSetting,
  hasUnresolvedShift,
  overtimeRequestMinutes,
  borderLeftStyleDirection,
  hasEditTimesheetAuthorization,
  editTimesheetRequests,
  inconsistencies,
  showStartsNextDayCheckbox,
  estimatedTime,
  trackedMinutes,
  workedMinutes,
  effectiveWorkedMinutes,
  breaksMinutesRounded,
  hasCorrectiveBreaks,
  hasExtraHourManagementV2,
  showExtraHoursColumn,
  showComplementaryHoursColumn,
  workedMinutesNotApproved,
  shifts,
}: ShiftRowAccordionProps) => {
  const commonProps = {
    store,
    shiftLocations,
    setBackground,
    open,
    hasFutureDate,
    date,
    calendarDay,
    trackedMinutes,
    shifts,
    inconsistencies,
  }

  const attendanceSettingIds = useMemo(() => {
    return attendanceSetting?.id ? [attendanceSetting.id] : []
  }, [attendanceSetting?.id])
  // TODO: this needs to be from the big timesheet query not fetch it again
  const { breakSettings } = useFetchBreakDurationRules(attendanceSettingIds)
  const breakSetting = breakSettings[0]

  const restTimeInconsistencies =
    breakSetting?.restTime &&
    isBreakSetting(breakSetting) &&
    breakSettings.length > 0 &&
    !BreakSettingsUtils.hasAutofixMissingBreaks(breakSetting)
      ? validateBreakDurationRules(shifts, breakSetting)
      : []

  return (
    <>
      <RowTab
        toggle={toggle}
        dateKey={dateKey}
        className={className}
        workedBreaks={workedBreaks}
        isBeforeToday={isBeforeToday}
        showHourBalance={showHourBalance}
        hasUnresolvedShift={hasUnresolvedShift}
        borderLeftStyleDirection={borderLeftStyleDirection}
        restTimeInconsistencies={restTimeInconsistencies}
        balanceMinutes={balanceMinutes}
        attendanceSetting={attendanceSetting}
        estimatedTime={estimatedTime}
        workedMinutes={workedMinutes}
        effectiveWorkedMinutes={effectiveWorkedMinutes}
        breaksMinutesRounded={breaksMinutesRounded}
        hasCorrectiveBreaks={hasCorrectiveBreaks}
        hasExtraHourManagementV2={hasExtraHourManagementV2}
        showExtraHoursColumn={showExtraHoursColumn}
        showComplementaryHoursColumn={showComplementaryHoursColumn}
        allBreaks={allBreaks}
        workedMinutesNotApproved={workedMinutesNotApproved}
        {...commonProps}
      />
      <RowPanel
        dayBreaks={dayBreaks}
        allBreaks={allBreaks}
        attendanceSetting={attendanceSetting}
        overtimeRequestMinutes={overtimeRequestMinutes}
        hasEditTimesheetAuthorization={hasEditTimesheetAuthorization}
        editTimesheetRequests={editTimesheetRequests}
        restTimeInconsistencies={restTimeInconsistencies}
        showStartsNextDayCheckbox={showStartsNextDayCheckbox}
        estimatedTime={estimatedTime}
        hasCorrectiveBreaks={hasCorrectiveBreaks}
        hasExtraHourManagementV2={hasExtraHourManagementV2}
        showExtraHoursColumn={showExtraHoursColumn}
        {...commonProps}
      />
    </>
  )
}

export default observer(ShiftRowAccordion)
